import {
  EnhancedPlan,
  SubscriptionResponse,
} from '@mm/state/interfaces/subscription';
import ConnectedPageModel from '@mm/state/model/ConnectedPageModel';
import { SubscriptionModel } from '@mm/state/model/SubscriptionModel';
import modalActions from '@mm/state/modules/modalsState/actions';
import { activeFacebookPageSelector } from '@mm/state/selectors/facebookPageSelector';
import {
  defaultTeamPlanSelector,
  enhancedPlansSelector,
} from '@mm/state/selectors/subscriptionSelectors';
import { includes } from 'lodash';
import moment from 'moment';
import React, { useContext, useMemo } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { PrimaryButton } from '../../../../../../../common/buttons/PrimaryButton/PrimaryButton';
import DropdownMenu from '../../../../../../../common/components/DropdownMenu/DropdownMenu';
import { useOpenConditionalDowngradeModal } from '../../../../../../../common/hooks/use-open-conditional-downgrade-modal';
import { BillingPannelContext } from '../../../BillingPanel';
import NewTeamAddPagesButton from './NewTeamAddPagesButton/NewTeamAddPagesButton';
import './TeamPlanButtonsArea.pcss';

type Props = {
  showUpgradeButton: boolean;
  teamSubscription: SubscriptionModel;
  accountDetails?: SubscriptionResponse;
  pages: ConnectedPageModel[];
  plan: EnhancedPlan;
  searchInput: string;
  setSearchInput: (searchInput: string) => void;
  upgradePlansIds: number[];
  facebookPageId?: number;
};

const getPlanExpiresText = (
  teamSubscription: SubscriptionModel,
  accountDetails?: SubscriptionResponse,
) => {
  const isCanceled = teamSubscription.state === 'canceled';

  if (accountDetails && isCanceled) {
    return `Plan expires ${moment(teamSubscription.expiresAt).format(
      'MM/DD/YYYY',
    )}`;
  }
};

const useDropdownMenuList = (props: Props) => {
  const { teamSubscription, plan, pages, facebookPageId } = props;
  const dispatch = useDispatch();

  const { openConditionalDowngradeModal } = useOpenConditionalDowngradeModal();
  return useMemo(() => {
    const openManagePagesModal = (
      subscription: SubscriptionModel,
      plan: EnhancedPlan,
      pagesCount: number,
    ) =>
      dispatch(
        modalActions.openManagePagesModal({
          subscription,
          plan,
          pagesCount,
          facebookPageId,
        }),
      );

    if (teamSubscription.state === 'canceled') {
      return [];
    }

    const manageItem = {
      name: 'Manage additional page slots',
      onClick: () => openManagePagesModal(teamSubscription, plan, pages.length),
    };

    const cancelItem = {
      name: 'Cancel subscription',
      onClick: () =>
        openConditionalDowngradeModal({ subscription: teamSubscription, plan }),
    };

    if (teamSubscription.pagesLimit) {
      return [manageItem, cancelItem];
    }

    return [cancelItem];
  }, [teamSubscription, plan, pages, openConditionalDowngradeModal, dispatch]);
};

const getCanUpgradeToPlans = (
  plans: EnhancedPlan[],
  upgradePlansIds: number[],
) => {
  return plans.filter(plan => includes(upgradePlansIds, plan.id));
};

export const TeamPlanButtonsArea = (props: Props) => {
  const {
    showUpgradeButton,
    teamSubscription,
    plan,
    accountDetails,
    pages,
    searchInput,
    setSearchInput,
    upgradePlansIds,
  } = props;
  const openSlots = teamSubscription.pagesLimit
    ? teamSubscription.pagesLimit - pages.length
    : 0;
  const defaulTeamPlan = useSelector(defaultTeamPlanSelector, shallowEqual);
  const facebookPage = useSelector(activeFacebookPageSelector, shallowEqual);
  const plans = useSelector(enhancedPlansSelector, shallowEqual);
  const dropdownMenuList = useDropdownMenuList(props);
  const planExpiresText = getPlanExpiresText(teamSubscription, accountDetails);

  const dispatch = useDispatch();
  const { landmineOpenId } = useContext(BillingPannelContext);

  const openBillingModal = (
    plan: EnhancedPlan,
    upgradeFromSubscriptionId: number,
  ) =>
    dispatch(
      modalActions.openBillingModal({
        plan,
        upgradeFromSubscriptionId,
        facebookPage,
        landmineOpenId,
        upgradetoTeam: true,
        subscription: facebookPage?.subscription,
      }),
    );

  const openBillingModalHandler = () => {
    openBillingModal(defaulTeamPlan as EnhancedPlan, teamSubscription.id);
  };

  return (
    <div className="team-plan-buttons-area">
      {showUpgradeButton &&
        getCanUpgradeToPlans(plans, upgradePlansIds).length && (
          <PrimaryButton
            onClick={openBillingModalHandler}
            className="team-plan-buttons-area__upgrade-button"
          >
            Upgrade to Agency
          </PrimaryButton>
        )}
      {teamSubscription.pagesLimit !== null && (
        <>
          <div className="team-plan-buttons-area__open-slots">
            {openSlots} OPEN SLOTS REMAINING
          </div>

          <NewTeamAddPagesButton
            searchInput={searchInput}
            setSearchInput={setSearchInput}
            totalPages={pages.length}
            teamSubscription={teamSubscription}
            teamPlan={plan}
          />
        </>
      )}

      {!!dropdownMenuList.length && (
        <DropdownMenu
          className="team-plan-buttons-area__more-dropdown"
          menuItems={dropdownMenuList}
          menuButton={onClick => (
            <div
              className="team-plan-buttons-area__more-dropdown-button"
              onClick={onClick}
            >
              <div className="team-plan-buttons-area__more-dropdown-button-circle" />
              <div className="team-plan-buttons-area__more-dropdown-button-circle" />
              <div className="team-plan-buttons-area__more-dropdown-button-circle" />
            </div>
          )}
        />
      )}

      {planExpiresText && (
        <div className="team-plan-buttons-area__team-expires">
          {planExpiresText}
        </div>
      )}
    </div>
  );
};
